import { useThemeColor } from "@/hooks/useThemeColor";
import { useNavigation, useRouter } from "expo-router";
import React from "react";
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from "react-native";
import type { ThemedTextProps } from "./ThemedText";
import { ThemedText } from "./ThemedText";
import { ThemedView } from "./ThemedView";
import { IconSymbol } from "./ui/IconSymbol";

export type CustomHeaderVariant = "default" | "tab";

export function CustomHeader({
  title,
  children,
  containerStyle,
  childrenStyle,
  type,
  variant = "default",
  showBackButton,
  onBackPress,
}: {
  title?: string;
  containerStyle?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
  childrenStyle?: StyleProp<ViewStyle>;
  type?: ThemedTextProps["type"];
  variant?: CustomHeaderVariant;
  showBackButton?: boolean;
  onBackPress?: () => void;
}) {
  const router = useRouter();
  const navigation = useNavigation();
  const defaultColor = useThemeColor({}, "text");
  const tabBackground = useThemeColor({}, "tabBarBackground");
  const tabForeground = useThemeColor({}, "tabIconActive");

  const isTab = variant === "tab";
  const color = isTab ? tabForeground : defaultColor;
  const variantContainerStyle = isTab
    ? { backgroundColor: tabBackground, borderBottomWidth: 0 }
    : undefined;
  const variantInnerStyle = isTab
    ? { backgroundColor: tabBackground }
    : undefined;

  const handleBackPress = () => {
    if (onBackPress) {
      onBackPress();
      return;
    }

    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      router.replace("/");
    }
  };

  return (
    <ThemedView
      role="banner"
      style={[styles.container, variantContainerStyle, containerStyle]}
    >
      {showBackButton && (
        <TouchableOpacity
          onPress={handleBackPress}
          style={styles.backButton}
          testID="header-back-button"
          accessibilityRole="button"
          accessibilityLabel="Volver"
        >
          <IconSymbol name="chevron.left" size={24} color={color} />
        </TouchableOpacity>
      )}
      <ThemedView style={[styles.titleContainer, variantInnerStyle]}>
        {title ? (
          <ThemedText
            type={type ? type : "title"}
            numberOfLines={1}
            ellipsizeMode="tail"
            style={[{ textAlign: "center" }, isTab && { color }]}
          >
            {title}
          </ThemedText>
        ) : (
          <ThemedView style={[childrenStyle, variantInnerStyle]}>
            {React.Children.toArray(children)}
          </ThemedView>
        )}
      </ThemedView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 60,
    justifyContent: "center",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    flexDirection: "row",
    paddingHorizontal: 60,
  },
  backButton: {
    position: "absolute",
    left: 10,
    padding: 10,
  },
  titleContainer: {
    flex: 1,
  },
});
